import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LanguageContext";
import { navigation, labels } from "@/data/content";
import { X, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
const martinezLogo = "/martinez-logo-sm.webp";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const { t, getLocalizedHref } = useLocale();

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-sm bg-background border-border/30 p-0 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border/30">
          <img src={martinezLogo} alt="Martinez Law" className="h-12 w-auto" width="250" height="112" />
          <button onClick={onClose} className="p-2.5 text-muted-foreground hover:text-foreground" aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-b border-border/30">
          <a
            href="https://office.martinez.law/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center gap-2 text-base font-medium text-primary hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-5 w-5" />
            {t(labels.clientPortal)}
          </a>
          <LanguageSwitcher variant="compact" />
        </div>

        <nav className="flex-1 overflow-y-auto p-5" aria-label="Mobile navigation">
          <Accordion type="single" collapsible>
            {navigation.map((item) => {
              if (item.children) {
                return (
                  <AccordionItem key={item.href} value={item.href} className="border-border/30">
                    <AccordionTrigger className="text-foreground hover:text-primary hover:no-underline py-4 text-lg">
                      {t(item.label)}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="flex flex-col gap-1.5 pl-4">
                        {item.children.map((child) => (
                          child.children ? (
                            <div key={child.href} className="mt-2 mb-1">
                              <Link to={getLocalizedHref(child.href, child.esHref)} onClick={onClose} className="block py-2 text-base font-semibold text-foreground hover:text-primary transition-colors">
                                {t(child.label)}
                              </Link>
                              <div className="flex flex-col gap-1 pl-3 border-l border-border/30">
                                {child.children.map((sub) => (
                                  <Link key={sub.href} to={getLocalizedHref(sub.href, sub.esHref)} onClick={onClose} className="py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    {t(sub.label)}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Link key={child.href} to={getLocalizedHref(child.href, child.esHref)} onClick={onClose} className="py-2.5 text-base text-muted-foreground hover:text-foreground transition-colors">
                              {t(child.label)}
                            </Link>
                          )
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              }
              return (
                <div key={item.href} className="border-b border-border/30">
                  <Link
                    to={getLocalizedHref(item.href, item.esHref)}
                    onClick={onClose}
                    className={cn(
                      "block py-4 text-lg transition-colors",
                      item.highlight
                        ? "text-primary font-bold"
                        : "text-foreground hover:text-primary"
                    )}
                  >
                    {t(item.label)}
                    {item.highlight && (
                      <span className="ml-2 inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </Link>
                </div>
              );
            })}
          </Accordion>
        </nav>

        <div className="p-5 border-t border-border/30 space-y-4">
          <a href="tel:+17738183020" className="block w-full text-center py-4 border border-primary/60 text-primary rounded-md text-lg font-medium hover:bg-primary/10 transition-colors">
            {t(labels.callNow)} — (773) 818-3020
          </a>
          <Link to={getLocalizedHref("/contact")} onClick={onClose} className="block w-full text-center py-4 bg-ivory-cta text-ivory-cta-foreground rounded-md text-lg font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
            {t(labels.bookConsultation)}
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
